import React from "react";
import { PrivacyTermsModal } from "./PrivacyTermsModal";
import { StayConnectedDropdown } from "./StayConnectedDropdown";
import { MivaltaLandingModalProps } from "../../../lib/MivaltaLandingModalProps";
import PartnersSection from "./PartnersSection";
import logo from "../../../logo.png";

export default function MobileLanding({ setModal }: MivaltaLandingModalProps) {
  const [openModal, setOpenModal] = React.useState<null | "privacy" | "terms">(
    null
  );

  return (
    <div
      className="min-h-screen bg-center bg-cover bg-no-repeat pt-4 text-white font-sans flex flex-col"
      style={{ backgroundImage: "url('/mivalta_background.png')" }}
    >
      <main className="flex-1">
        {/* Header */}
        <header className="sticky top-0 z-50 backdrop-blur-md border-b border-black mb-6">
          <div className="flex justify-between items-center py-3 px-2">
            <div className="flex items-center font-bold text-xl">
              <img src={logo} alt="MiValta logo" className="w-10 h-10 mr-2" />
              MiValta
            </div>
            <div className="text-sm underline">
              <StayConnectedDropdown onSelectModal={setModal} />
            </div>
          </div>
        </header>

        {/* Main Text */}
        <section className="text-center mb-10 px-2">
          {/* Hero header */}
          <h1 className="text-5xl font-extrabold leading-snug mb-8">
            Your Body.
            <br />
            Your Data.
          </h1>

          {/* Normal body text = text-lg */}
          <p className="text-lg leading-relaxed mb-4">
            MiValta is the privacy-first activity & training platform where all
            your data comes together.
          </p>
          <p className="text-lg leading-relaxed mb-4">
            Track and plan your training. Unlock AI-powered insights. Stay in
            full control and ownership of your data.
          </p>

          {/* Italic text = text-sm */}
          <p className="italic text-sm mt-6 text-gray-300">
            With MiValta, you don’t trade your data for features. You get both
            privacy and performance.
          </p>
        </section>

        <hr className="my-10 w-3/4 mx-auto border-white/20" />

        {/* Why We’re Building MiValta */}
        <section className="mb-10 px-4 text-center">
          {/* Block header = text-3xl */}
          <h2 className="text-3xl font-extrabold mb-6">
            Why We’re Building MiValta
          </h2>

          <div className="space-y-6 text-lg leading-relaxed text-gray-200">
            <p>
              Most fitness and activity platforms treat your data as their
              product.
            </p>
            <p className="text-lg mt-2 text-gray-300">
              No one should have to sacrifice privacy to use a training app.
            </p>
            <p className="text-lg mt-2 text-gray-300">
              Your movement is personal. Your progress is yours. We’re building
              the future of training — one that respects you.
            </p>
            <p className="italic text-sm mt-2 text-gray-300">
              Founded by an Olympic champion and high-performance coach.
            </p>
          </div>
        </section>

        <hr className="my-10 w-3/4 mx-auto border-white/20" />

        {/* Join as a Tester */}
        <section className="text-center mb-10 px-4">
          <h2 className="text-3xl font-extrabold mb-6">
            Be Part of the Foundation
          </h2>

          <div className="space-y-5 text-lg leading-relaxed text-gray-200">
            <p>
              We’re looking for cyclists and runners who care about performance
              and privacy.
            </p>
            <p>Join our beta phase and help shape the future of MiValta.</p>
          </div>

          <button
            className="bg-[#23c8a1] text-white text-sm font-semibold uppercase tracking-wide px-6 py-3 mt-8 mb-8 rounded hover:brightness-110 transition"
            onClick={() => setModal("tester")}
          >
            Join as a Tester
          </button>

          <p className="italic text-sm text-white">
            Be the first to train with AI that respects you.
          </p>
        </section>

        <hr className="my-10 w-3/4 mx-auto border-white/20" />

        {/* The MiValta Difference */}
        <section className="mb-10">
          <h2 className="text-3xl font-extrabold text-center mb-10">
            The MiValta Difference
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
            {[
              {
                icon: "/icons/private.png",
                title: "Private by Design",
                desc: "Your data stays with you. No leaks. No third parties. No compromises.",
              },
              {
                icon: "/icons/science.png",
                title: "Science, Not Trends",
                desc: "Built on evidence-based training — not hype, gimmicks, or fads.",
              },
              {
                icon: "/icons/ai.png",
                title: "Real Coaching Intelligence",
                desc: "Our AI, Josi, adapts to your body, rhythm, and goals — running locally on your device.",
              },
            ].map(({ icon, title, desc }) => (
              <div key={title} className="text-center px-4">
                <div className="mb-4 flex justify-center">
                  <img src={icon} alt={title} className="w-12 h-12" />
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p
                  className="text-lg text-gray-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: desc }}
                />
              </div>
            ))}
          </div>
        </section>

        <hr className="my-10 w-3/4 mx-auto border-white/20" />

        {/* Who It's For */}
        <section className="mb-10 px-4 text-center">
          {/* Changed title + block header sizing */}
          <h2 className="text-3xl font-extrabold mb-6">Move On Your Terms</h2>

          <div className="space-y-4 text-lg leading-relaxed text-gray-200">
            <p>
              From seasoned competitors to lifelong movers; if you believe you
              shouldn’t have to sacrifice privacy to improve performance, you
              belong here.
            </p>
          </div>
        </section>

        <hr className="my-10 w-3/4 mx-auto border-white/20" />

        {/* Help build MiValta */}
        <section className="text-center mb-12 px-4">
          <h2 className="text-3xl font-extrabold mb-6">Help Build MiValta</h2>
          <p className="mb-6 text-lg text-gray-200 leading-relaxed">
            Are you a developer, designer, creator, or supporter of ethical
            tech? We’re building something bigger — and we’d love to have you
            with us.
          </p>
          <button
            className="bg-[#23c8a1] text-white text-sm font-semibold uppercase tracking-wide px-6 py-3 rounded hover:brightness-110 transition"
            onClick={() => setModal("build")}
          >
            Join MiValta
          </button>
        </section>

        <hr className="my-10 w-3/4 mx-auto border-white/20" />

        <PartnersSection />
      </main>
      {/* Footer */}
      <footer className="mt-auto text-center text-xs opacity-60">
        © 2025 MiValta. Ethics at its core. All rights reserved. |{" "}
        <button className="underline" onClick={() => setOpenModal("privacy")}>
          Privacy Promise
        </button>{" "}
        |{" "}
        <button className="underline" onClick={() => setOpenModal("terms")}>
          Terms
        </button>
      </footer>

      <PrivacyTermsModal
        openModal={openModal}
        onClose={() => setOpenModal(null)}
      />
    </div>
  );
}
